import * as exifr from "exifr";

export type PreparedImage = {
  sourceSha256: string;
  originalFilename: string;
  takenAt: string | null;
  master: Blob;
  thumb: Blob;
  width: number;
  height: number;
};

function hex(buffer: ArrayBuffer) {
  return [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function decode(file: File): Promise<Blob> {
  try {
    await createImageBitmap(file);
    return file;
  } catch {
    if (!/heic|heif/i.test(file.type + file.name)) throw new Error("Tento formát fotografie telefon nepodporuje.");
    const converter = (await import("heic2any")).default;
    const converted = await converter({ blob: file, toType: "image/jpeg", quality: 0.9 });
    return Array.isArray(converted) ? converted[0] : converted;
  }
}

async function canvasJpeg(blob: Blob, longestEdge: number, quality: number) {
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, longestEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("Fotografii se nepodařilo připravit.");
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const jpeg = await new Promise<Blob>((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error("Fotografii se nepodařilo převést.")), "image/jpeg", quality));
  return { jpeg, width, height };
}

export async function prepareImage(file: File): Promise<PreparedImage> {
  const sourceSha256 = hex(await crypto.subtle.digest("SHA-256", await file.arrayBuffer()));
  const exif = await exifr.parse(file, { pick: ["DateTimeOriginal", "CreateDate"] }).catch(() => null) as { DateTimeOriginal?: Date; CreateDate?: Date } | null;
  const takenAt = exif?.DateTimeOriginal?.toISOString() ?? exif?.CreateDate?.toISOString() ?? (file.lastModified ? new Date(file.lastModified).toISOString() : null);
  const decoded = await decode(file);
  const master = await canvasJpeg(decoded, 2560, 0.88);
  const thumb = await canvasJpeg(decoded, 480, 0.78);
  return { sourceSha256, originalFilename: file.name, takenAt, master: master.jpeg, thumb: thumb.jpeg, width: master.width, height: master.height };
}
